import { matchPath } from 'react-router-dom';
import { DEFAULT_META_DESCRIPTION, SITE_NAME } from './site';

/** i18n keys under `seo.routes.*` plus English fallbacks for `t(key, fallback)`. */
export interface RouteSeoRow {
  titleKey: string;
  descKey: string;
  titleFb: string;
  descFb: string;
}

const FB = {
  homeTitle: `${SITE_NAME} — Quizzes, PvP arena & career`,
  loginTitle: `Login — ${SITE_NAME}`,
  loginDesc: `Sign in with Google to save progress, compete on leaderboards, and unlock achievements on ${SITE_NAME}.`,
  nickTitle: `Choose nickname — ${SITE_NAME}`,
  nickDesc: `Pick your player name to appear on leaderboards and in PvP matches before you continue on ${SITE_NAME}.`,
  lbTitle: `Leaderboards — ${SITE_NAME}`,
  lbDesc: `See top players by XP level and achievement score. Compare ranks and profiles on ${SITE_NAME}.`,
  catTitle: `Achievement catalog — ${SITE_NAME}`,
  catDesc: `Browse badges and milestones you can unlock by playing quizzes, PvP, and career missions on ${SITE_NAME}.`,
  invTitle: `Inventory — ${SITE_NAME}`,
  invDesc: `Manage XP potions and items from missions and rewards. Use boosts strategically on ${SITE_NAME}.`,
  achTitle: `Achievements — ${SITE_NAME}`,
  achDesc: `Track unlocked achievements and points. Earn rewards across quizzes, PvP, and career content on ${SITE_NAME}.`,
  profAchTitle: `Player achievements — ${SITE_NAME}`,
  profAchDesc: `Achievement list and scores for this player’s progress on ${SITE_NAME}.`,
  profPubTitle: `Player profile — ${SITE_NAME}`,
  profPubDesc: `Public profile with level, achievements, and rankings for this player on ${SITE_NAME}.`,
  profMineTitle: `My profile — ${SITE_NAME}`,
  profMineDesc: `View your level, XP, VIP status, and stats. Edit settings and share your ${SITE_NAME} progress.`,
  settingsTitle: `Settings — ${SITE_NAME}`,
  settingsDesc: `Language, notifications, and account preferences for your ${SITE_NAME} experience.`,
  vipTitle: `VIP membership — ${SITE_NAME}`,
  vipDesc: `VIP perks: double lives with faster recharge, double base XP, and highlighted nickname during your subscription on ${SITE_NAME}.`,
  notifTitle: `Notifications — ${SITE_NAME}`,
  notifDesc: `Alerts for friends, progression, achievements, lives, and PvP updates on ${SITE_NAME}.`,
  adminTitle: `Admin — ${SITE_NAME}`,
  adminDesc: `Administrative tools for trusted accounts on ${SITE_NAME}. Sign in required.`,
  pvpTitle: `PvP match — ${SITE_NAME}`,
  pvpDesc: `Real-time head-to-head quiz room. Answer fast, earn bonus XP, and finish before your opponent on ${SITE_NAME}.`,
  quizTitle: `Quiz — ${SITE_NAME}`,
  quizDesc: `Timed interactive quiz with multiple-choice questions, XP rewards, and results on ${SITE_NAME}.`,
  notFoundTitle: `Page not found — ${SITE_NAME}`,
  notFoundDesc: DEFAULT_META_DESCRIPTION,
};

const ROUTE_TABLE: Array<{ pattern: string } & RouteSeoRow> = [
  {
    pattern: '/',
    titleKey: 'seo.routes.home.title',
    descKey: 'seo.routes.home.desc',
    titleFb: FB.homeTitle,
    descFb: DEFAULT_META_DESCRIPTION,
  },
  {
    pattern: '/about',
    titleKey: 'seo.routes.about.title',
    descKey: 'seo.routes.about.desc',
    titleFb: `About — ${SITE_NAME}`,
    descFb: `Explore modes, FAQs, achievements, XP, PvP arena, career missions, and leaderboards on ${SITE_NAME}.`,
  },
  {
    pattern: '/contact',
    titleKey: 'seo.routes.contact.title',
    descKey: 'seo.routes.contact.desc',
    titleFb: `Contact — ${SITE_NAME}`,
    descFb: `Send a suggestion or bug report while signed in. Short plain-text message on ${SITE_NAME}.`,
  },
  {
    pattern: '/privacy',
    titleKey: 'seo.routes.privacy.title',
    descKey: 'seo.routes.privacy.desc',
    titleFb: `Privacy Policy — ${SITE_NAME}`,
    descFb: `How ${SITE_NAME} collects, uses, and protects your data. Available in English and Portuguese.`,
  },
  {
    pattern: '/login',
    titleKey: 'seo.routes.login.title',
    descKey: 'seo.routes.login.desc',
    titleFb: FB.loginTitle,
    descFb: FB.loginDesc,
  },
  {
    pattern: '/nickname-setup',
    titleKey: 'seo.routes.nicknameSetup.title',
    descKey: 'seo.routes.nicknameSetup.desc',
    titleFb: FB.nickTitle,
    descFb: FB.nickDesc,
  },
  {
    pattern: '/leaderboard',
    titleKey: 'seo.routes.leaderboard.title',
    descKey: 'seo.routes.leaderboard.desc',
    titleFb: FB.lbTitle,
    descFb: FB.lbDesc,
  },
  {
    pattern: '/achievements/catalog',
    titleKey: 'seo.routes.achievementsCatalog.title',
    descKey: 'seo.routes.achievementsCatalog.desc',
    titleFb: FB.catTitle,
    descFb: FB.catDesc,
  },
  {
    pattern: '/inventory',
    titleKey: 'seo.routes.inventory.title',
    descKey: 'seo.routes.inventory.desc',
    titleFb: FB.invTitle,
    descFb: FB.invDesc,
  },
  {
    pattern: '/achievements',
    titleKey: 'seo.routes.achievements.title',
    descKey: 'seo.routes.achievements.desc',
    titleFb: FB.achTitle,
    descFb: FB.achDesc,
  },
  {
    pattern: '/profile/:userId/achievements',
    titleKey: 'seo.routes.profileAchievements.title',
    descKey: 'seo.routes.profileAchievements.desc',
    titleFb: FB.profAchTitle,
    descFb: FB.profAchDesc,
  },
  {
    pattern: '/profile/:userId',
    titleKey: 'seo.routes.profilePublic.title',
    descKey: 'seo.routes.profilePublic.desc',
    titleFb: FB.profPubTitle,
    descFb: FB.profPubDesc,
  },
  {
    pattern: '/profile',
    titleKey: 'seo.routes.profileMine.title',
    descKey: 'seo.routes.profileMine.desc',
    titleFb: FB.profMineTitle,
    descFb: FB.profMineDesc,
  },
  {
    pattern: '/settings',
    titleKey: 'seo.routes.settings.title',
    descKey: 'seo.routes.settings.desc',
    titleFb: FB.settingsTitle,
    descFb: FB.settingsDesc,
  },
  {
    pattern: '/vip',
    titleKey: 'seo.routes.vip.title',
    descKey: 'seo.routes.vip.desc',
    titleFb: FB.vipTitle,
    descFb: FB.vipDesc,
  },
  {
    pattern: '/notifications',
    titleKey: 'seo.routes.notifications.title',
    descKey: 'seo.routes.notifications.desc',
    titleFb: FB.notifTitle,
    descFb: FB.notifDesc,
  },
  {
    pattern: '/admin',
    titleKey: 'seo.routes.admin.title',
    descKey: 'seo.routes.admin.desc',
    titleFb: FB.adminTitle,
    descFb: FB.adminDesc,
  },
  {
    pattern: '/pvp-quiz/:roomId',
    titleKey: 'seo.routes.pvpQuiz.title',
    descKey: 'seo.routes.pvpQuiz.desc',
    titleFb: FB.pvpTitle,
    descFb: FB.pvpDesc,
  },
  {
    pattern: '/quiz/:id',
    titleKey: 'seo.routes.quiz.title',
    descKey: 'seo.routes.quiz.desc',
    titleFb: FB.quizTitle,
    descFb: FB.quizDesc,
  },
];

const NOT_FOUND_ROW: RouteSeoRow = {
  titleKey: 'seo.routes.notFound.title',
  descKey: 'seo.routes.notFound.desc',
  titleFb: FB.notFoundTitle,
  descFb: FB.notFoundDesc,
};

export function resolveRouteSeoRow(pathname: string): RouteSeoRow {
  for (const row of ROUTE_TABLE) {
    const m = matchPath({ path: row.pattern, end: true }, pathname);
    if (m) {
      return {
        titleKey: row.titleKey,
        descKey: row.descKey,
        titleFb: row.titleFb,
        descFb: row.descFb,
      };
    }
  }
  return NOT_FOUND_ROW;
}
